import type { ThemeConfig } from '@chakra-ui/react'
import type { GlobalProps } from '@emotion/react'
import { fonts } from './fonts'

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false
}

const breakpoints = {
  xs: '30em',
  sm: '40em',
  md: '52em',
  lg: '64em',
  xl: '80em'
}

// Granditude color palette from HTML design
const tealColorSchema = {
  900: '#0d5a63',
  800: '#0f6770',
  700: '#12747d',
  600: '#15818a',
  500: '#1b8591', // Primary accent
  400: '#2a929e',
  300: '#3ea0ab',
  200: '#5fb3bd',
  100: '#8fc9d0',
  50: '#c5e3e7'
}

const amberColorSchema = {
  900: '#8a5d00',
  800: '#a36e00',
  700: '#bf8200',
  600: '#d99300',
  500: '#f6a800', // Secondary accent
  400: '#ffb81c',
  300: '#ffc94d',
  200: '#ffd97a',
  100: '#ffe9a8',
  50: '#fff5d6'
}

const grayColorSchema = {
  950: '#2d3540',
  900: '#3f4855',
  800: '#4a5362',
  700: '#565f6a',
  600: '#6b7380',
  500: '#7f8694',
  400: '#9599a5',
  300: '#abafb8',
  200: '#c2c5cc',
  100: '#d9dbdf',
  50: '#ecf0f1'
}

export const baseTheme = {
  config,
  breakpoints,
  styles: {
    global: (_props: GlobalProps) => ({
      html: {
        scrollBehavior: 'smooth'
      },
      body: {
        bg: 'background.default',
        color: 'text.default',
        fontFamily: 'body',
        fontSize: '17px',
        lineHeight: 1.6,
        WebkitFontSmoothing: 'antialiased'
      },
      '#__next': {},
      ':root': {}
    })
  },
  colors: {
    teal: tealColorSchema,
    amber: amberColorSchema,
    gray: grayColorSchema,
    brand: grayColorSchema,
    primary: tealColorSchema,
    secondary: amberColorSchema,
    klarna: '#1b8591',
    background: {
      default: '#fefefe',
      surface: '#ecf0f1',
      muted: '#f5f7f8',
      accent: '#1b8591',
      mildDark: '#3f4855',
      dark: '#2d3540',
      darkest: '#1a1f25',
      translucent: 'rgba(236, 240, 241, 0.90)',
      overlayHalf: 'rgba(0,0,0, 0.25)',
      overlay: 'rgba(0,0,0, 0.5)',
      primary: 'rgba(27, 133, 145, 0.1)',
      secondary: 'rgba(246, 168, 0, 0.1)'
    },
    text: {
      default: '#000000',
      primary: '#000000',
      secondary: '#565f6a',
      mild: '#565f6a',
      light: '#ffffff',
      muted: '#7f8694',
      inverse: '#ffffff',
      onDark: '#ffffff',
      darkGrey: '#2d3540',
      colored: '#1b8591',
      accent: '#1b8591'
    },
    border: {
      default: '#cfe0e5',
      light: '#e8eef0',
      dark: '#3f4855',
      muted: '#d9dbdf'
    },
    status: {
      success: '#1dc395',
      warning: '#f6a800',
      warning2: '#fff5d6',
      error: '#f35f5f'
    }
  },
  semanticTokens: {
    colors: {
      'chakra-body-text': { _light: 'text.default', _dark: 'whiteAlpha.900' },
      primary: {
        default: 'teal.500'
      },
      accent: {
        default: 'teal.500'
      }
    }
  },
  textStyles: {
    display: {
      fontFamily: 'var(--chakra-fonts-display)',
      fontWeight: 500,
      lineHeight: 1.15,
      letterSpacing: '-0.03em'
    },
    sectionTitle: {
      fontFamily: 'var(--chakra-fonts-display)',
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '-0.02em'
    },
    sectionLabel: {
      fontFamily: 'var(--chakra-fonts-body)',
      fontSize: '0.8rem',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: 'teal.500'
    },
    bodyLarge: {
      fontFamily: 'var(--chakra-fonts-body)',
      fontSize: '1.1rem',
      lineHeight: 1.7,
      color: 'text.secondary'
    }
  },
  shadows: {
    outline: '0 0 0 2px #1b8591',
    primary: '0 0 0 2px #1b8591',
    secondary: '0 0 0 2px #f6a800',
    primaryBlur: '0 0 4px 2px #1b8591',
    secondaryBlur: '0 0 4px 2px #f6a800',
    card: '0 4px 12px rgba(0, 0, 0, 0.1)',
    cardHover: '0 12px 40px rgba(0, 0, 0, 0.08)',
    chaos:
      '0 4px 12px rgba(0, 0, 0, 0.1), 0 8px 30px rgba(0, 0, 0, 0.12), 0 15px 45px rgba(0, 0, 0, 0.08)'
  },
  radii: {
    card: '12px',
    button: '8px',
    pill: '24px'
  },
  space: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2.5rem',
    xl: '4rem',
    '2xl': '6rem',
    '3xl': '8rem'
  },
  sizes: {
    maxWidth: '1200px',
    maxWidthSm: '800px',
    maxWidthNarrow: '600px'
  },
  fonts
}
